import type { ReactNode } from "react";
import styles from "./Card.module.css";

export interface CardProps {
  children: ReactNode;
  className?: string;
}

export function Card({ children, className }: CardProps): ReactNode {
  return (
    <section className={[styles.card, className].filter(Boolean).join(" ")}>{children}</section>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  /** One sentence saying what the panel counts, or what it leaves out. */
  description?: ReactNode;
  actions?: ReactNode;
}

export function CardHeader({ title, description, actions }: CardHeaderProps): ReactNode {
  return (
    <div className={styles.header}>
      <div className={styles.heading}>
        <div className={styles.title}>{title}</div>
        {description !== undefined && (
          // Clamped to one line (see the CSS); the title carries the rest, for
          // the reader who wants to know what the panel leaves out.
          <div
            className={styles.description}
            {...(typeof description === "string" ? { title: description } : {})}
          >
            {description}
          </div>
        )}
      </div>
      {actions !== undefined && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}

/** Padded card body, for anything that isn't a flush-to-the-edge table. */
export function CardBody({ children, className }: CardProps): ReactNode {
  return <div className={[styles.body, className].filter(Boolean).join(" ")}>{children}</div>;
}

export interface PageHeadingProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}

export function PageHeading({ title, subtitle, actions }: PageHeadingProps): ReactNode {
  return (
    <div className={styles.pageHead}>
      <div className={styles.heading}>
        <h1 className={styles.pageTitle}>{title}</h1>
        {subtitle !== undefined && <div className={styles.pageSubtitle}>{subtitle}</div>}
      </div>
      {actions !== undefined && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}
